'use client';

import React, { type ReactElement } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { WitroMobileBottomNavigation } from '@/features/shop/components/witro-mobile-nav.component';
import {
  selectDomain,
  selectIsTenant,
  useDomainStore,
} from '@/store/use-domain.store';
import classNames from 'classnames';
import Button from '@/common/components/button';
import Hamburger from '@/common/components/hamburger';
import StoreWidget from '@/common/components/store-widget';
import Wrapper from '@/common/components/wrapper/wrapper.component';
import { useBackNavigation } from '@/common/hooks/use-back-navigation.hook';
import { useShowAppbarSearch } from '@/common/hooks/use-show-appbar-search.hook';
import ArrowRightIcon from '~/public/assets/images/svg/arrow.svg';
import Logo from '~/public/assets/images/svg/logo-just-text.svg';
import SearchIcon from '~/public/assets/images/svg/search.svg';
import { type AppbarProps } from './appbar.component';

const CartWidget = dynamic(
  async () => await import('@/common/components/cart-widget'),
  {
    ssr: false,
  }
);

export default function MobileAppbar({
  onBack,
  storeWidget,
  cartWidget = true,
  hamburger = true,
  title,
  titlePosition,
  className,
  logo = false,
  showSearch = true,
  fixedHeader,
  layoutChildrenClassNames,
  wrapperClassName,
}: AppbarProps): ReactElement {
  const { handleBack } = useBackNavigation();
  const isTenant = useDomainStore(selectIsTenant);
  const config = useDomainStore(selectDomain);
  const shouldShowSearch = useShowAppbarSearch(showSearch);

  const pathname = usePathname();
  const hasCollectionInUrl = pathname?.includes('collection');

  const onBackHandler = () => {
    if (onBack) onBack();
    handleBack();
  };

  const renderSearchComponent = () => {
    return (
      <div className="bg-silver-100 px-4 py-3">
        <div className="flex w-full flex-row items-center justify-between rounded-xl border border-silver-200 bg-white px-4 py-3.5">
          <Link
            prefetch
            href={'/search'}
            className="w-full text-sm font-normal leading-5 text-black-200"
          >
            {isTenant ? `جست و جو در ${config?.title}` : 'جست و جو در ویترو'}
          </Link>
          <SearchIcon className="h-5 w-5" />
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className={classNames(
          'w-full bg-white lg:hidden',
          fixedHeader ? '!fixed left-0 right-0 top-0 !z-[100]' : '',
          className
        )}
      >
        <Wrapper className={classNames('px-3', wrapperClassName)}>
          <div
            className={classNames(
              'flex items-center bg-white p-3 shadow-sm',
              {
                'justify-between': storeWidget || !title,
              }
            )}
          >
            <div
              className={classNames(
                'flex min-w-0 items-center justify-start gap-x-2',
                storeWidget || !title ? 'flex-1' : 'w-10 shrink-0'
              )}
            >
              {hasCollectionInUrl ? (
                <Button
                  className="flex !h-10 w-10 items-center justify-center"
                  type="link"
                  onClick={onBackHandler}
                >
                  <ArrowRightIcon
                    className={classNames(
                      'h-3 w-[8px] text-black-default',
                      layoutChildrenClassNames?.arrowClassName
                    )}
                  />
                </Button>
              ) : isTenant ? (
                <Link
                  href="/"
                  className="flex min-w-0 items-center gap-x-2"
                >
                  {config?.logoUrl && (
                    <div className="relative h-[50px] w-[50px] shrink-0">
                      <Image
                        src={config.logoUrl}
                        alt={config.title || 'لوگوی فروشگاه'}
                        fill
                        className="object-contain"
                        sizes="50px"
                      />
                    </div>
                  )}
                  {config?.title && (
                    <h1 className="truncate text-sm font-semibold text-black-default">
                      {config.title}
                    </h1>
                  )}
                </Link>
              ) : logo ? (
                <Link href="/">
                  <Logo className="h-[20px]" />
                </Link>
              ) : (
                <Button
                  className="flex !h-10 w-10 items-center justify-center"
                  type="link"
                  onClick={onBackHandler}
                >
                  <ArrowRightIcon
                    className={classNames(
                      'h-3 w-[8px] text-black-default',
                      layoutChildrenClassNames?.arrowClassName
                    )}
                  />
                </Button>
              )}
              {storeWidget && (
                <StoreWidget
                  titleClassName={
                    layoutChildrenClassNames?.storeWidgetTitleClassName
                  }
                  btnLinkClassName={
                    layoutChildrenClassNames?.storeWidgetBtnLinkClassName
                  }
                  isTenant={isTenant}
                  displayHeading={false}
                />
              )}
            </div>

            {title && (
              <h3
                className={classNames(
                  'min-w-0 flex-1 truncate text-center text-[14px] font-medium text-black-default',
                  { 'text-right': titlePosition === 'right' }
                )}
              >
                {title}
              </h3>
            )}

            <div className="flex shrink-0 items-center justify-end gap-x-2">
              {cartWidget && (
                <CartWidget
                  id="store-click-cart-v1"
                  iconClassName={layoutChildrenClassNames?.cartClassName}
                />
              )}
              {hamburger && (
                <Hamburger
                  iconClassName={layoutChildrenClassNames?.hamburgerClassName}
                />
              )}
            </div>
          </div>
        </Wrapper>

        {shouldShowSearch ? renderSearchComponent() : null}
      </div>
      <WitroMobileBottomNavigation />
    </>
  );
}
